import {useCallback, useRef, useEffect} from 'react';

/**
 * React hook that returns a function to send Riskified tracking.
 * Waits for the RISKX library to be available and retries if not loaded yet.
 * Automatically cleans up pending timers on unmount.
 *
 * @param {Object} options - Configuration options
 * @param {number} options.maxAttempts - Maximum number of retry attempts (default: 3)
 * @param {number} options.delayMs - Delay between attempts in milliseconds (default: 250)
 *
 * @returns {Function} Function to trigger Riskified tracking
 *
 * @example
 * const sendRiskified = useRiskifiedGo();
 * // Later, when event fires:
 * sendRiskified();
 */
export const useRiskifiedGo = (options = {}, logger) => {
  const {maxAttempts = 3, delayMs = 250} = options;
  const timeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const sendRiskified = useCallback(() => {
    let attempts = 0;

    const tryExecute = () => {
      attempts += 1;

      if (typeof window !== 'undefined' && window.RISKX && typeof window.RISKX.go === 'function') {
        try {
          const currentUrl = window.location.pathname;
          window.RISKX.go(currentUrl);
        } catch (error) {
          logger?.error('Error executing Riskified:', error);
        }
      } else if (attempts < maxAttempts) {
        timeoutRef.current = setTimeout(tryExecute, delayMs);
      } else {
        logger?.error(`RISKX library not available after ${maxAttempts} attempts`);
      }
    };

    tryExecute();
  }, [maxAttempts, delayMs, logger]);

  return sendRiskified;
};
